const BASE = "https://strapi.bidayatech.agency";

export const strapiUrl = BASE;

// ── Auth helpers ──────────────────────────────────────────────────────────────

export function getToken(): string | null {
  return localStorage.getItem("admin_jwt");
}

export function getUser(): { id: number; username: string; email: string } | null {
  const raw = localStorage.getItem("admin_user");
  return raw ? JSON.parse(raw) : null;
}

export function saveSession(jwt: string, user: object) {
  localStorage.setItem("admin_jwt", jwt);
  localStorage.setItem("admin_user", JSON.stringify(user));
}

export function clearSession() {
  localStorage.removeItem("admin_jwt");
  localStorage.removeItem("admin_user");
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ── Core fetch helpers ────────────────────────────────────────────────────────

async function apiFetch(path: string, options: RequestInit = {}) {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers: {
      ...authHeaders(),
      ...(options.headers as Record<string, string> | undefined),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message ?? `Erreur API ${res.status}`);
  }
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

// ── Authentication ─────────────────────────────────────────────────────────────

export async function strapiLogin(identifier: string, password: string) {
  // Ne pas utiliser apiFetch ici : il injecterait un ancien JWT invalide
  // via authHeaders(), ce qui provoquerait un 401 côté Strapi.
  clearSession(); // Nettoie toute session précédente avant d'authentifier
  const res = await fetch(`${BASE}/api/auth/local`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identifier, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message ?? `Erreur API ${res.status}`);
  }
  const data = await res.json();
  saveSession(data.jwt, data.user);
  return data as { jwt: string; user: { id: number; username: string; email: string } };
}

// ── Upload ────────────────────────────────────────────────────────────────────

export async function strapiUpload(file: File): Promise<{ id: number; url: string }> {
  const jwt = getToken();

  if (!jwt) throw new Error("Non authentifié — reconnectez-vous");

  const fd = new FormData();
  fd.append("files", file);

  const res = await fetch(`${BASE}/api/upload`, {
    method: "POST",
    headers: { Authorization: `Bearer ${jwt}` },
    body: fd,
  });

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    throw new Error(
      errBody?.error?.message ?? `Upload refusé (${res.status})`
    );
  }
  const data = await res.json();
  return data[0];
}

// ── Blocks helpers (Strapi v5 "blocks" content type) ─────────────────────────

type TextMark = { type: "text"; text: string; bold?: boolean; italic?: boolean; underline?: boolean; strikethrough?: boolean };
type Block = { type: string; level?: number; format?: string; children: Array<TextMark | { type: string; children: TextMark[] }> };

function inlineToHTML(children: unknown[]): string {
  return (children ?? []).map((c) => {
    const m = c as TextMark;
    if (!m.text && m.text !== "") return "";
    let t = m.text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    if (m.bold) t = `<strong>${t}</strong>`;
    if (m.italic) t = `<em>${t}</em>`;
    if (m.underline) t = `<u>${t}</u>`;
    if (m.strikethrough) t = `<s>${t}</s>`;
    return t;
  }).join("");
}

export function blocksToHTML(blocks: unknown): string {
  if (!Array.isArray(blocks)) return "";
  return (blocks as Block[]).map((b) => {
    switch (b.type) {
      case "heading":
        return `<h${b.level ?? 2}>${inlineToHTML(b.children as unknown[])}</h${b.level ?? 2}>`;
      case "list": {
        const tag = b.format === "ordered" ? "ol" : "ul";
        const items = (b.children as { children: unknown[] }[])
          .map((item) => `<li>${inlineToHTML(item.children)}</li>`)
          .join("");
        return `<${tag}>${items}</${tag}>`;
      }
      case "quote":
        return `<blockquote>${inlineToHTML(b.children as unknown[])}</blockquote>`;
      default:
        return `<p>${inlineToHTML(b.children as unknown[])}</p>`;
    }
  }).join("");
}

export function blocksToText(blocks: unknown): string {
  if (!Array.isArray(blocks)) return String(blocks ?? "");
  return (blocks as Block[])
    .map((b) => (b.children as TextMark[])?.map((c) => c.text ?? "").join("") ?? "")
    .join("\n");
}

export function htmlToBlocks(html: string): Block[] {
  if (!html?.trim()) return [{ type: "paragraph", children: [{ type: "text", text: "" }] }];
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  function parseInline(node: Node): TextMark[] {
    const results: TextMark[] = [];
    function walk(n: Node, marks: Omit<TextMark, "type" | "text"> = {}) {
      if (n.nodeType === Node.TEXT_NODE) {
        const text = n.textContent ?? "";
        if (text) results.push({ type: "text", text, ...marks });
        return;
      }
      if (n.nodeType !== Node.ELEMENT_NODE) return;
      const tag = (n as Element).tagName.toLowerCase();
      const m = { ...marks };
      if (tag === "strong" || tag === "b") m.bold = true;
      if (tag === "em" || tag === "i") m.italic = true;
      if (tag === "u") m.underline = true;
      if (tag === "s" || tag === "strike") m.strikethrough = true;
      for (const child of Array.from(n.childNodes)) walk(child, m);
    }
    walk(node);
    return results.length > 0 ? results : [{ type: "text", text: "" }];
  }

  const blocks: Block[] = [];
  for (const node of Array.from(doc.body.childNodes)) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      if (text) blocks.push({ type: "paragraph", children: [{ type: "text", text }] });
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue;
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    if (/^h[1-6]$/.test(tag)) {
      blocks.push({ type: "heading", level: parseInt(tag[1]), children: parseInline(el) });
    } else if (tag === "ul" || tag === "ol") {
      const items = Array.from(el.querySelectorAll(":scope > li")).map((li) => ({
        type: "list-item",
        children: parseInline(li),
      }));
      blocks.push({ type: "list", format: tag === "ol" ? "ordered" : "unordered", children: items });
    } else if (tag === "blockquote") {
      blocks.push({ type: "quote", children: parseInline(el) });
    } else {
      const children = parseInline(el);
      if (children.some((c) => c.text)) blocks.push({ type: "paragraph", children });
    }
  }
  return blocks.length > 0 ? blocks : [{ type: "paragraph", children: [{ type: "text", text: "" }] }];
}

// ── Blog ──────────────────────────────────────────────────────────────────────
// Schema champs : title, content (blocks), image (media multiple), Date (date)

export interface BlogPost {
  id: number;
  documentId: string;    // Strapi v5 — identifiant stable pour les requêtes GET/:id
  title: string;
  content: string;       // converti depuis blocks en texte
  date: string;          // champ "Date" dans Strapi
  image?: { id: number; url: string } | null;
  author?: { id: number; username: string; email: string } | null;
}

function resolveUrl(url: string): string {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${BASE}${url}`;
}

function mapBlog(item: Record<string, unknown>): BlogPost {
  // Strapi v5 = flat, v4 = { attributes }
  const a = (item.attributes as Record<string, unknown>) ?? item;

  // Résoudre l'image selon le format Strapi v4 ou v5
  let image: { id: number; url: string } | null = null;
  const imgField = a.image;

  if (Array.isArray(imgField) && imgField.length > 0) {
    const img = imgField[0] as Record<string, unknown>;
    image = { id: img.id as number, url: resolveUrl((img.url as string) ?? "") };
  } else if (imgField && typeof imgField === "object" && "url" in (imgField as object)) {
    const img = imgField as Record<string, unknown>;
    image = { id: img.id as number, url: resolveUrl((img.url as string) ?? "") };
  } else if (imgField && typeof imgField === "object" && "data" in (imgField as object)) {
    const data = (imgField as { data?: { id: number; attributes?: { url: string } } | Array<{ id: number; attributes?: { url: string } }> }).data;
    const img = Array.isArray(data) ? data[0] : data;
    if (img) {
      image = { id: img.id, url: resolveUrl(img.attributes?.url ?? "") };
    }
  }

  const authorField = a.owner_id ?? a.author ?? a.users_permissions_user;
  const author = authorField && typeof authorField === "object"
    ? (authorField as Record<string, unknown>)
    : null;

  return {
    id: item.id as number,
    documentId: (item.documentId as string) ?? "",
    title: (a.Titre as string) ?? (a.title as string) ?? "",
    content: blocksToText(a.content),
    date: (a.Date as string) ?? (a.date as string) ?? "",
    image,
    author: author ? {
      id: author.id as number,
      username: (author.username as string) ?? "",
      email: (author.email as string) ?? "",
    } : null,
  };
}

export async function getBlogs(): Promise<BlogPost[]> {
  const qs = new URLSearchParams({
    "populate[0]": "image",
    "sort[0]": "Date:desc",
    "pagination[pageSize]": "100",
  });
  const user = getUser();
  if (user?.id) {
    qs.set("filters[owner_id][$eq]", String(user.id));
  }
  const data = await apiFetch(`/api/blogs?${qs}`);
  return (data.data as Record<string, unknown>[]).map(mapBlog);
}

export async function getBlog(documentId: string): Promise<BlogPost> {
  const data = await apiFetch(`/api/blogs/${documentId}?populate[0]=image`);
  return mapBlog(data.data as Record<string, unknown>);
}

function imagePayload(imageId?: number) {
  if (!imageId) return {};
  return { image: imageId };
}

export async function createBlog(payload: {
  title: string;
  content: string;
  date: string;
  imageId?: number;
}) {
  return apiFetch("/api/blogs", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      data: {
        Titre: payload.title,
        content: htmlToBlocks(payload.content),
        Date: payload.date,
        publishedAt: new Date().toISOString(),
        ...imagePayload(payload.imageId),
      },
    }),
  });
}

export async function updateBlog(
  id: string,
  payload: { title: string; content: string; date: string; imageId?: number }
) {
  return apiFetch(`/api/blogs/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      data: {
        Titre: payload.title,
        content: htmlToBlocks(payload.content),
        Date: payload.date,
        publishedAt: new Date().toISOString(),
        ...imagePayload(payload.imageId),
      },
    }),
  });
}

export async function deleteBlog(documentId: string) {
  return apiFetch(`/api/blogs/${documentId}`, { method: "DELETE" });
}

// ── Public Blog (sans auth) ───────────────────────────────────────────────────

export async function getPublicBlogs(): Promise<BlogPost[]> {
  const qs = new URLSearchParams({
    "populate[0]": "image",
    "sort[0]": "Date:desc",
    "pagination[pageSize]": "100",
    "status": "published",
  });
  const siteUserId = import.meta.env.VITE_STRAPI_SITE_USER_ID;
  if (siteUserId) {
    qs.set("filters[owner_id][$eq]", String(siteUserId));
  }
  const res = await fetch(`${BASE}/api/blogs?${qs}`);
  if (!res.ok) throw new Error(`Erreur API ${res.status}`);
  const data = await res.json();
  return (data.data as Record<string, unknown>[]).map(mapBlog);
}

// ── Forms (lecture seule — admin) ─────────────────────────────────────────────
// Schema champs : Nom, Email, Telephone, Sujet, message (blocks)

export interface FormEntry {
  id: number;
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  createdAt: string;
}

export async function getForms(): Promise<FormEntry[]> {
  if (!getToken()) throw new Error("Non authentifié");
  const qs = new URLSearchParams({
    "sort[0]": "createdAt:desc",
    "pagination[pageSize]": "100",
    "status": "draft",          // inclure les entrées en mode brouillon
  });
  const data = await apiFetch(`/api/forms?${qs}`);
  return (data.data as Record<string, unknown>[]).map((item) => {
    const a = (item.attributes as Record<string, unknown>) ?? item;
    return {
      id: item.id as number,
      name:      (a.Nom     as string) ?? (a.Name    as string) ?? (a.name    as string) ?? "",
      email:     (a.Email   as string) ?? (a.email   as string) ?? "",
      phone:     String((a.Telephone as string | number | undefined) ?? (a.Phone as string | number | undefined) ?? (a.phone as string | number | undefined) ?? ""),
      subject:   (a.Sujet   as string) ?? (a.Subject as string) ?? (a.subject as string) ?? "",
      message:   blocksToText(a.message),
      createdAt: (a.createdAt as string) ?? "",
    };
  });
}

// ── Public Form submit (sans auth) ────────────────────────────────────────────

export async function submitPublicForm(payload: {
  name: string;
  email: string;
  phone?: string;
  subject?: string;
  message: string;
}) {
  // Noms exacts des champs dans le schema Strapi : Nom, Email, Telephone, Sujet, message
  const data: Record<string, unknown> = {
    Nom:     payload.name,
    Email:   payload.email,
    message: htmlToBlocks(payload.message),
  };
  if (payload.phone)   data.Telephone = payload.phone;
  if (payload.subject) data.Sujet     = payload.subject;

  const res = await fetch(`${BASE}/api/forms`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ data }),
  });

  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    const msg = errBody?.error?.message ?? errBody?.error?.details ?? `Erreur ${res.status}`;
    throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
  }
  return res.json();
}
